// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDisableTwoFactorMutation, useProfileQuery } from "@/api/hooks/use-profile";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { TwoFactorSetupDialog } from "@/features/profile/two-factor-setup-dialog";

export function TwoFactorSection() {
  const { t } = useTranslation("back-office");
  const { data: profile, isLoading, isError } = useProfileQuery();
  const disable = useDisableTwoFactorMutation();
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  async function onDisable() {
    try {
      await disable.mutateAsync();
      toast.success(t("profile.security.mfa.disabled"));
      setDisableOpen(false);
    } catch {
      toast.error(t("profile.security.mfa.disableError"));
    }
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-medium">{t("profile.security.mfa.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("profile.security.mfa.description")}</p>
      </div>

      {isLoading ? (
        <Skeleton className="h-14 w-full" />
      ) : isError || !profile ? (
        // Don't fall through to "Set up two-factor" on a load failure — that reads as a definitive
        // "no MFA" when the status is simply unknown. Surface the error like the Info tab. CIN-676.
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {t("errors.loadFailed")}
        </div>
      ) : profile.hasMfa ? (
        <div className="flex items-center gap-3 rounded-md border p-3">
          <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium">{t("profile.security.mfa.statusLabel")}</span>
              <Badge variant="secondary" className="-mt-px shrink-0 text-[10px]">
                {t("profile.security.mfa.enabledStatus")}
              </Badge>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 text-destructive hover:text-destructive"
            onClick={() => setDisableOpen(true)}
            aria-label={t("profile.security.mfa.disable")}
          >
            <ShieldOff className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setSetupOpen(true)} className="gap-2">
          <ShieldCheck className="h-4 w-4" />
          {t("profile.security.mfa.setup")}
        </Button>
      )}

      <TwoFactorSetupDialog open={setupOpen} onOpenChange={setSetupOpen} />

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden flex flex-col">
          <div className="flex flex-row">
            <WizardPane icon={ShieldOff} variant="destructive" />
            <div className="flex flex-1 min-w-0 flex-col gap-4 p-6">
              <DialogHeader>
                <DialogTitle>{t("profile.security.mfa.disableTitle")}</DialogTitle>
                <DialogDescription>{t("profile.security.mfa.disableConfirm")}</DialogDescription>
              </DialogHeader>
            </div>
          </div>
          <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
            <Button type="button" variant="outline" onClick={() => setDisableOpen(false)}>
              {t("buttons.cancel")}
            </Button>
            <Button variant="destructive" onClick={onDisable} disabled={disable.isPending}>
              {disable.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
              {t("profile.security.mfa.disable")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
